import { IAlbum } from '@/data/models/album';
import { ITrack } from '@/data/models/track';
import { cloneDeep } from 'lodash';

export type MetadataField =
  | 'title'
  | 'version'
  | 'artists'
  | 'genre'
  | 'cLine'
  | 'pLine';

interface CopyProductMetadataParams {
  album: IAlbum;
  allTracks: ITrack[];
  selectedTrackIds: number[];
  selectedFields: MetadataField[];
  overwriteValues: boolean;
}

const isBlank = (value?: string | null) => !value?.trim();

const isCopyrightEmpty = (track: ITrack) =>
  isBlank(track.copyrightLine) && !track.copyrightYear;

const isPhonographicCopyrightEmpty = (track: ITrack) =>
  isBlank(track.phonographicCopyrightLine) && !track.phonographicCopyrightYear;

function copyFieldToTrack(
  track: ITrack,
  album: IAlbum,
  field: MetadataField,
  overwriteValues: boolean
): ITrack {
  switch (field) {
    case 'title':
      if (overwriteValues || isBlank(track.title)) {
        return { ...track, title: album.title || '' };
      }
      return track;

    case 'version':
      if (overwriteValues || isBlank(track.version)) {
        return {
          ...track,
          version: album.albumMetaData?.versions?.at(0) ?? '',
        };
      }
      return track;

    case 'artists':
      if (overwriteValues || !track.artists?.length) {
        return {
          ...track,
          artists: album.artists ? cloneDeep(album.artists) : [],
          displayArtist: album.displayArtist || '',
        };
      }
      return track;

    case 'genre':
      if (overwriteValues || !track.genre) {
        return {
          ...track,
          genre: album.albumMetaData?.genre
            ? cloneDeep(album.albumMetaData.genre)
            : undefined,
        };
      }
      return track;

    case 'cLine':
      if (overwriteValues || isCopyrightEmpty(track)) {
        return {
          ...track,
          copyrightLine: album.albumMetaData?.copyrightLine || '',
          copyrightYear: album.albumMetaData?.copyrightYear || undefined,
        };
      }
      return track;

    case 'pLine':
      if (overwriteValues || isPhonographicCopyrightEmpty(track)) {
        return {
          ...track,
          phonographicCopyrightLine:
            album.albumMetaData?.phonographicCopyrightLine || '',
          phonographicCopyrightYear:
            album.albumMetaData?.phonographicCopyrightYear ?? undefined,
        };
      }
      return track;

    default:
      return track;
  }
}

export function copyProductMetadataToTracks({
  album,
  allTracks,
  selectedTrackIds,
  selectedFields,
  overwriteValues,
}: CopyProductMetadataParams): IAlbum {
  const selectedTrackIdSet = new Set(selectedTrackIds);

  const updatedTracks = allTracks.map((track) => {
    if (track.id && !selectedTrackIdSet.has(track.id)) return track;

    return selectedFields.reduce(
      (updatedTrack, field) =>
        copyFieldToTrack(updatedTrack, album, field, overwriteValues),
      track
    );
  });

  return {
    ...cloneDeep(album),
    tracks: updatedTracks,
  };
}
